import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Popconfirm, Typography } from 'antd';
import { retryMemberFactSync } from '@/services/technical';

export function newRetryRequestId() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(
    '',
  );
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
    12,
    16,
  )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export default function FactSyncRetryButton({ task, memberId, roomId }) {
  const [state, setState] = useState({});
  const busy = useRef(false);
  const request = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  // 仅为按钮提示；真实资格及最新状态由服务端锁定任务后校验。
  const eligible =
    task.status === 'failed' &&
    task.attemptCount >= 5 &&
    task.updatedAt > 0 &&
    task.eventType !== 'unknown';
  const retry = async () => {
    if (busy.current || state.accepted || state.conflict || !eligible) return;
    busy.current = true;
    setState({ loading: true });
    try {
      if (!request.current)
        request.current = {
          memberId,
          roomId,
          taskRecordId: task.id,
          expectedAttemptCount: task.attemptCount,
          expectedUpdatedAt: task.updatedAt,
          requestId: newRetryRequestId(),
        };
      const response = await retryMemberFactSync({
        requestData: request.current,
      });
      const d = response?.data;
      if (
        !['00000', '0'].includes(String(response?.code)) ||
        String(d?.memberId) !== String(memberId) ||
        d?.roomId !== roomId ||
        d?.taskRecordId !== task.id ||
        d?.requestId !== request.current.requestId ||
        !['accepted', 'replayed'].includes(d?.outcome)
      )
        throw new Error('Unconfirmed retry');
      if (mounted.current) setState({ accepted: true });
    } catch (error) {
      const status = error?.response?.status;
      if (mounted.current)
        setState({
          conflict: status === 409,
          error:
            {
              401: '请先登录后台',
              403: '无会员档案菜单权限',
              409: '状态已变化或不符合重试条件，请刷新课程状态',
            }[status] || '结果未确认，再次点击将沿用同一请求；也可刷新课程状态',
        });
    } finally {
      busy.current = false;
    }
  };
  if (!eligible)
    return (
      <Typography.Text type="secondary">不符合人工重试条件</Typography.Text>
    );
  if (state.accepted)
    return (
      <Typography.Text>
        重试请求已受理，请刷新课程状态；不代表同步完成
      </Typography.Text>
    );
  return (
    <>
      <Popconfirm
        title="重新入队这条失败任务？后续可能更新档案并触发模型调用与费用，不保证重试成功。"
        onConfirm={retry}
        okText="确认重新入队"
        cancelText="取消"
        disabled={state.loading || state.conflict}
      >
        <Button
          size="small"
          loading={state.loading}
          disabled={state.loading || state.conflict}
        >
          重试同步
        </Button>
      </Popconfirm>
      {state.error && (
        <Typography.Paragraph type="warning">
          {state.error}
        </Typography.Paragraph>
      )}
    </>
  );
}
FactSyncRetryButton.propTypes = {
  task: PropTypes.object.isRequired,
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  roomId: PropTypes.number.isRequired,
};
